import React, { useState, useEffect, useCallback } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from "recharts";
import { Users, Eye, Globe, MapPin, FileText, Monitor, ExternalLink, RefreshCw, LogOut } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pn_admin_token";
const ORANGE = "#E2611C";
const INK = "#16181D";

const COUNTRY_FLAGS = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#e4e9f0] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#6b7280]">
        <Icon size={16} className="text-[#E2611C]" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="mt-3 font-head text-3xl font-semibold tracking-tight text-[#16181D]">{value}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-2xl border border-[#e4e9f0] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[#E2611C]" />
        <h2 className="font-head text-base font-semibold text-[#16181D]">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function BarList({ items, labelKey, render, max }) {
  const top = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="text-sm text-[#9aa0a8]">Noch keine Daten.</p>;
  return (
    <ul className="space-y-2.5">
      {items.slice(0, max || 99).map((it, i) => (
        <li key={i} className="text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="truncate text-[#16181D]">{render ? render(it) : it[labelKey]}</span>
            <span className="shrink-0 font-mono text-xs text-[#6b7280]">{it.count}</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#f0f2f5]">
            <div className="h-full rounded-full bg-[#E2611C]" style={{ width: `${(it.count / top) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminStats() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (tok, rangeDays) => {
    if (!tok) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/analytics/stats?days=${rangeDays}`, { headers: { "x-admin-token": tok } });
      if (res.status === 401) { setError("Ungültiger Token."); setData(null); localStorage.removeItem(TOKEN_KEY); setToken(""); return; }
      if (!res.ok) { setError("Fehler beim Laden der Statistik."); return; }
      setData(await res.json());
    } catch (e) {
      setError("Verbindungsfehler.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token, days); }, [token, days, load]);

  const handleLogin = (e) => {
    e.preventDefault();
    const t = input.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setData(null); };

  if (!token) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="admin-login">
        <form onSubmit={handleLogin} className="w-full max-w-sm rounded-2xl border border-[#e4e9f0] bg-white p-8">
          <h1 className="font-head text-2xl font-semibold text-[#16181D]">Statistik-Login</h1>
          <p className="mt-2 text-sm text-[#6b7280]">Bitte Admin-Token eingeben, um die Besucherstatistik zu sehen.</p>
          <input
            type="password" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Admin-Token" data-testid="admin-token-input"
            className="mt-5 w-full rounded-lg border border-[#e4e9f0] px-4 py-3 text-sm outline-none focus:border-[#E2611C]"
          />
          {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-submit"
            className="mt-5 w-full rounded-lg bg-[#16181D] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#E2611C]">
            Anmelden
          </button>
        </form>
      </div>
    );
  }

  const t = data?.totals || { visitors: 0, pageviews: 0, days: 0 };
  const perDay = (data?.per_day || []).map((d) => ({ ...d, label: d.day.slice(5) }));

  return (
    <div className="bg-[#f6f8fb] py-10 md:py-14" data-testid="admin-stats">
      <div className="pn-container">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline">PlastoNord · Analytics</p>
            <h1 className="mt-1 font-head text-3xl font-semibold tracking-tight text-[#16181D]">Besucherstatistik</h1>
            {data && <p className="mt-1 text-sm text-[#6b7280]">Zeitraum: seit {data.since} · {data.range_days} Tage · DSGVO-konform, keine IP-Speicherung</p>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-[#e4e9f0] bg-white">
              {[7, 30, 90].map((d) => (
                <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}`}
                  className={`px-3.5 py-2 text-sm font-medium transition-colors ${days === d ? "bg-[#16181D] text-white" : "text-[#16181D] hover:bg-[#f6f8fb]"}`}>
                  {d}T
                </button>
              ))}
            </div>
            <button onClick={() => load(token, days)} data-testid="refresh-btn" title="Aktualisieren"
              className="rounded-lg border border-[#e4e9f0] bg-white p-2.5 text-[#16181D] hover:text-[#E2611C]">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} data-testid="admin-logout" title="Abmelden"
              className="rounded-lg border border-[#e4e9f0] bg-white p-2.5 text-[#16181D] hover:text-[#E2611C]">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {error && <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {/* Totals */}
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          <StatCard icon={Users} label="Eindeutige Besucher" value={t.visitors.toLocaleString("de-DE")} testid="stat-visitors" />
          <StatCard icon={Eye} label="Seitenaufrufe" value={t.pageviews.toLocaleString("de-DE")} testid="stat-pageviews" />
          <StatCard icon={FileText} label="Aktive Tage" value={t.days.toLocaleString("de-DE")} testid="stat-days" />
        </div>

        {/* Per-day chart */}
        <div className="mt-6 rounded-2xl border border-[#e4e9f0] bg-white p-6" data-testid="chart-per-day">
          <h2 className="font-head text-base font-semibold text-[#16181D]">Besucher & Aufrufe pro Tag</h2>
          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={perDay} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ORANGE} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={ORANGE} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={INK} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={INK} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9aa0a8" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9aa0a8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e4e9f0", fontSize: 13 }}
                  labelFormatter={(l) => `Tag ${l}`}
                  formatter={(v, n) => [v, n === "visitors" ? "Besucher" : "Aufrufe"]} />
                <Area type="monotone" dataKey="pageviews" stroke={INK} strokeWidth={2} fill="url(#gP)" />
                <Area type="monotone" dataKey="visitors" stroke={ORANGE} strokeWidth={2.5} fill="url(#gV)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-5 text-xs text-[#6b7280]">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: ORANGE }} /> Besucher</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: INK }} /> Seitenaufrufe</span>
          </div>
        </div>

        {/* Grid of panels */}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title="Länder" icon={Globe} testid="panel-countries">
            <BarList items={data?.countries || []} max={12}
              render={(it) => <span>{COUNTRY_FLAGS(it.country_code)} {it.country}</span>} />
          </Panel>

          <Panel title="Regionen (Kontinente)" icon={MapPin} testid="panel-regions">
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.regions || []} layout="vertical" margin={{ left: 20, right: 10 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="region" width={110} tick={{ fontSize: 12, fill: "#16181D" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e4e9f0", fontSize: 13 }} formatter={(v) => [v, "Aufrufe"]} />
                  <Bar dataKey="count" fill={ORANGE} radius={[0, 6, 6, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Top-Seiten" icon={FileText} testid="panel-pages">
            <BarList items={data?.pages || []} max={12} render={(it) => <span className="font-mono text-[13px]">{it.path}</span>} />
          </Panel>

          <div className="grid gap-6">
            <Panel title="Geräte" icon={Monitor} testid="panel-devices">
              <BarList items={data?.devices || []} render={(it) => <span>{it.device}</span>} />
            </Panel>
            <Panel title="Quellen / Referrer" icon={ExternalLink} testid="panel-referrers">
              <BarList items={data?.referrers || []} max={8} render={(it) => <span>{it.referrer}</span>} />
            </Panel>
          </div>
        </div>

        <p className="mt-8 text-xs text-[#9aa0a8]">
          Hinweis: Es werden keine IP-Adressen gespeichert. Besucher werden pseudonymisiert (täglich rotierender Hash) gezählt –
          datenschutzfreundlich und ohne Drittanbieter.
        </p>
      </div>
    </div>
  );
}
